import * as React from "react";
import type { ReactNode } from "react";

interface DialogActionElement extends React.ReactElement {}

interface DialogOptions {
  id: string;
  open: boolean;
  title?: ReactNode;
  description?: ReactNode;
  content?: ReactNode;
  action?: DialogActionElement;
  className?: string;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
}

type DialogState = {
  dialogs: DialogOptions[];
};

const actionTypes = {
  ADD_DIALOG: "ADD_DIALOG",
  UPDATE_DIALOG: "UPDATE_DIALOG",
  DISMISS_DIALOG: "DISMISS_DIALOG",
} as const;

type ActionType = typeof actionTypes;

type Action =
  | {
      type: ActionType["ADD_DIALOG"];
      dialog: DialogOptions;
    }
  | {
      type: ActionType["UPDATE_DIALOG"];
      dialog: Partial<DialogOptions> & { id: string };
    }
  | {
      type: ActionType["DISMISS_DIALOG"];
      dialogId?: DialogOptions["id"];
    };

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

const listeners: Array<(state: DialogState) => void> = [];

let memoryState: DialogState = { dialogs: [] };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

function reducer(state: DialogState, action: Action): DialogState {
  switch (action.type) {
    case "ADD_DIALOG":
      return {
        ...state,
        dialogs: [action.dialog],
      };

    case "UPDATE_DIALOG":
      return {
        ...state,
        dialogs: state.dialogs.map((d) =>
          d.id === action.dialog.id ? { ...d, ...action.dialog } : d
        ),
      };

    case "DISMISS_DIALOG": {
      const { dialogId } = action;

      if (dialogId === undefined) {
        return {
          ...state,
          dialogs: [],
        };
      } else {
        return {
          ...state,
          dialogs: state.dialogs.filter((d) => d.id !== dialogId),
        };
      }
    }

    default:
      return state;
  }
}

type DialogContent = Omit<DialogOptions, "id" | "open"> & { onDismissCallback?: () => void };

function dialog({ onDismissCallback, ...props }: DialogContent) {
  const id = genId();

  const update = (props: Partial<DialogOptions>) =>
    dispatch({
      type: "UPDATE_DIALOG",
      dialog: { ...props, id },
    });
  const dismiss = () => dispatch({ type: "DISMISS_DIALOG", dialogId: id });

  dispatch({
    type: "ADD_DIALOG",
    dialog: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
        if (onDismissCallback) onDismissCallback();
      },
    },
  });

  return {
    id: id,
    dismiss,
    update,
  };
}

function useDialog() {
  const [state, setState] = React.useState<DialogState>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, []);

  return {
    ...state,
    dialog,
    dismiss: (dialogId?: string) => dispatch({ type: "DISMISS_DIALOG", dialogId }),
  };
}

export { useDialog, dialog };
